import { useCallback, useEffect, useState } from 'react'
import { notification } from 'antd'
import isNil from 'lodash/isNil'
import {
  useHubspotSyncMutation,
  useIsHubspotSyncing,
} from '../../api/client-billing-profile-api-client'
import { useRevokeCompanyAccessMutation } from '../../api/user-invitation-api-client'
import { RevokeCompanyAccessAction } from '../../api/dto/user-invitation.dto'
import { extractError } from '../../api/extract-error'
import { useFundingInviteAccess } from '../UserManagement/useFundingInviteAccess'
import {
  buildProfileUpdate,
  profilesLosingEveryRecipient,
  submitFailureReason,
  type ReviewRecipientRow,
} from './hubspotSync.helpers'

interface UseHubspotSyncReviewParams {
  rows: ReviewRecipientRow[]
  recipientsByProfileId: Record<number, string[]>
  /** Identifies the run these rows came from; a new one clears old errors. */
  runId: number
  /** Records the profile's new recipient list once the write lands. */
  onRemoved: (clientBillingProfileId: number, emails: string[]) => void
  /** Takes decided rows off the outstanding list, whether kept or deleted. */
  onResolved: (keys: string[]) => void
}

/** What each affected profile's recipient list becomes once these rows go. */
const groupRemaining = (
  rows: ReviewRecipientRow[],
  recipientsByProfileId: Record<number, string[]>
) => {
  const byProfile = new Map<
    number,
    { row: ReviewRecipientRow; dropped: Set<string> }
  >()
  for (const row of rows) {
    const entry = byProfile.get(row.clientBillingProfileId)
    if (entry) {
      entry.dropped.add(row.email.toLowerCase())
      continue
    }
    byProfile.set(row.clientBillingProfileId, {
      row,
      dropped: new Set([row.email.toLowerCase()]),
    })
  }

  return Array.from(byProfile.values()).map(({ row, dropped }) => ({
    row,
    emails: (recipientsByProfileId[row.clientBillingProfileId] ?? []).filter(
      (email) => !dropped.has(email.toLowerCase())
    ),
  }))
}

/**
 * The Needs review card. Delete is the only decision it writes: it drops the
 * address from the profile and, when this admin may, takes the person's access
 * to that company away with it. An address that should stay belongs back in
 * HubSpot, so dismissing the card leaves everything alone.
 */
export const useHubspotSyncReview = ({
  rows,
  recipientsByProfileId,
  runId,
  onRemoved,
  onResolved,
}: UseHubspotSyncReviewParams) => {
  const [pendingKeys, setPendingKeys] = useState<Record<string, boolean>>({})
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    setRowErrors({})
  }, [runId])

  const inviteAccess = useFundingInviteAccess()
  const syncMutation = useHubspotSyncMutation()
  const isSyncing = useIsHubspotSyncing()
  const revokeMutation = useRevokeCompanyAccessMutation()

  const revoke = useCallback(
    (deleted: ReviewRecipientRow[]) => {
      if (!inviteAccess.allowed || deleted.length === 0) return
      revokeMutation.mutate(
        {
          revocations: deleted.map((row) => ({
            email: row.email,
            personId: null,
            clientPublicKey: row.clientPublicKey,
            roleIds: inviteAccess.roleIds,
          })),
        },
        {
          // The bulk call reports a refused recipient inside a resolved
          // response rather than by rejecting, so the outcome has to be read
          // off the result. A skip is reported too: the recipient still came
          // off the profile, so staying quiet would read as access removed.
          onSuccess: (result) => {
            const describe = (items: typeof result.results) =>
              items
                .map(
                  (item) => `${item.email}: ${item.reason ?? 'Unknown reason'}`
                )
                .join('\n')

            const failed = result.results.filter(
              (item) => item.action === RevokeCompanyAccessAction.Failed
            )
            if (failed.length > 0) {
              notification.error({
                message:
                  'The recipient was removed, but revoking portal access failed',
                description: describe(failed),
              })
            }

            const skipped = result.results.filter(
              (item) => item.action === RevokeCompanyAccessAction.Skipped
            )
            if (skipped.length > 0) {
              notification.warning({
                message:
                  'The recipient was removed, but portal access was left alone',
                description: describe(skipped),
              })
            }
          },
        }
      )
    },
    [inviteAccess.allowed, inviteAccess.roleIds, revokeMutation.mutate]
  )

  const handleDelete = useCallback(
    (deleted: ReviewRecipientRow[], revokeEmails: ReadonlySet<string>) => {
      if (deleted.length === 0) return
      // The remaining-recipient list is read at click time, so a second delete
      // during a write would compute it from stale data and re-add the address
      // the first one removed. The same holds for a write from another panel.
      if (isSyncing) return
      const keys = deleted.map((row) => row.key)
      setPendingKeys((curr) => ({
        ...curr,
        ...Object.fromEntries(keys.map((key) => [key, true])),
      }))

      const updates = groupRemaining(deleted, recipientsByProfileId)
      syncMutation.mutate(
        {
          updates: updates.map(({ row, emails }) =>
            // A removal never grants: the addresses left keep the access they
            // already have, and this write must not hand out any more.
            buildProfileUpdate(row, emails, { accessEmails: [] })
          ),
        },
        {
          // One write covers every recipient dropped from the same profile, so
          // a refused profile keeps all of its rows on the list rather than
          // resolving them against a change that never landed.
          onSuccess: (result) => {
            const failed = result?.failed ?? []
            const refusedProfiles = new Map<number, string>()

            for (const { row, emails } of updates) {
              const error = submitFailureReason(failed, {
                clientBillingProfileId: row.clientBillingProfileId,
                clientPublicKey: row.clientPublicKey,
              })
              if (isNil(error)) {
                onRemoved(row.clientBillingProfileId, emails)
                continue
              }
              refusedProfiles.set(row.clientBillingProfileId, error)
            }

            const written = deleted.filter(
              (row) => !refusedProfiles.has(row.clientBillingProfileId)
            )
            onResolved(written.map((row) => row.key))
            revoke(
              written.filter((row) =>
                revokeEmails.has(row.email.trim().toLowerCase())
              )
            )

            setRowErrors((curr) => {
              const next = { ...curr }
              for (const key of written) delete next[key.key]
              for (const row of deleted) {
                const error = refusedProfiles.get(row.clientBillingProfileId)
                if (!isNil(error)) next[row.key] = error
              }
              return next
            })
            if (refusedProfiles.size === 0) return
            notification.warning({
              message: `${refusedProfiles.size === 1 ? 'A recipient' : 'Some recipients'} could not be removed`,
            })
          },
          onError: (error) => {
            notification.error({
              message: extractError(error, 'Failed to remove the recipient'),
            })
          },
          onSettled: () => {
            setPendingKeys((curr) => {
              const next = { ...curr }
              for (const key of keys) delete next[key]
              return next
            })
          },
        }
      )
    },
    [
      recipientsByProfileId,
      syncMutation.mutate,
      isSyncing,
      onRemoved,
      onResolved,
      revoke,
    ]
  )

  // Remove all takes everyone, a company's last recipient included. The
  // confirmation names any company left with nobody to invoice.
  const companiesLeftEmpty = useCallback(
    (removing: ReviewRecipientRow[]) => {
      const emptied = profilesLosingEveryRecipient(
        removing,
        recipientsByProfileId
      )
      return Array.from(
        new Set(
          removing
            .filter((row) => emptied.has(row.clientBillingProfileId))
            .map((row) => row.companyName)
        )
      )
    },
    [recipientsByProfileId]
  )

  return {
    rows,
    /** True while this row's write is in flight. */
    isPending: (key: string) => Boolean(pendingKeys[key]),
    /** Why the last delete refused this row; null until one does. */
    rowError: (key: string) => rowErrors[key] ?? null,
    isBusy: isSyncing,
    canDeleteAll: rows.length > 0,
    companiesLeftEmpty,
    /** `revokeEmails` holds the lower-cased addresses whose access comes off. */
    handleDelete: (
      row: ReviewRecipientRow,
      revokeEmails: ReadonlySet<string>
    ) => handleDelete([row], revokeEmails),
    handleDeleteAll: (revokeEmails: ReadonlySet<string>) =>
      handleDelete(rows, revokeEmails),
  }
}
